import { ContentsManager, KernelAPI, KernelManager, ServerConnection } from '@jupyterlab/services';
import type { IKernelConnection } from '@jupyterlab/services/lib/kernel/kernel';
import type { IExecuteReplyMsg, IIOPubMessage } from '@jupyterlab/services/lib/kernel/messages';
import * as Sentry from '@sentry/react';
import { mimeTypeForPath, type PreviewFileKind, previewFileKind } from '@/lib/notebook/fileTypes';
import type { WidgetManager } from './WidgetManager';

/**
 * Thrown when attempting to connect to a kernel that no longer exists
 * on the Jupyter server.
 *
 * This is an expected race between the webapp's cached kernel list and
 * the server: a kernel can die (crash, shutdown, OOM) between the moment
 * the UI reads `listRunning()` and the moment a consumer tries to attach.
 * Callers should self-heal (refresh the list) and surface a user-friendly
 * message rather than forwarding it to Sentry as an exception.
 */
export class KernelNotFoundError extends Error {
  override readonly name = 'KernelNotFoundError';
  readonly kernelId: string;

  constructor(kernelId: string) {
    super(`Kernel '${kernelId}' not found`);
    this.kernelId = kernelId;
  }
}

// Kernel execution status from Jupyter
export type KernelStatus =
  | 'unknown'
  | 'starting'
  | 'idle'
  | 'busy'
  | 'terminating'
  | 'restarting'
  | 'autorestarting'
  | 'dead';

// WebSocket connection status
export type KernelConnectionStatus = 'connecting' | 'connected' | 'disconnected';

/**
 * Classify a kernel death event for Sentry reporting.
 *
 * Returns 'ignore' for intentional teardowns, 'breadcrumb' for expected
 * deaths (startup failures, mid-execution crashes from Isaac Sim / OOM),
 * and 'error' for truly unexpected deaths worth investigating.
 */
export function classifyKernelDeath(
  prevStatus: KernelStatus,
  connectionStatus: KernelConnectionStatus,
  intentionalTeardown: boolean
): 'ignore' | 'breadcrumb' | 'error' {
  if (intentionalTeardown) return 'ignore';

  const wasEstablished = prevStatus !== 'unknown' && connectionStatus !== 'connecting';
  if (!wasEstablished) return 'breadcrumb';

  // Kernel died while executing code -- expected in simulation workloads
  // (Isaac Sim segfaults, OOM, workspace teardown while code is running)
  if (prevStatus === 'busy') return 'breadcrumb';

  // Kernel died while idle -- caused by external events the webapp cannot
  // control (Isaac Sim crash taking down the process, OOM killer, pod
  // eviction, workspace infrastructure teardown). These show up with
  // /sim/status 503s in the breadcrumbs preceding the death.
  if (prevStatus === 'idle') return 'breadcrumb';

  // Kernel died mid-restart or mid-auto-restart -- the Jupyter server was
  // already cycling the process; a death here is a server-side failure
  if (prevStatus === 'restarting' || prevStatus === 'autorestarting') return 'breadcrumb';

  return 'error';
}

// Combined kernel health for UI display
export interface KernelHealth {
  status: KernelStatus;
  connectionStatus: KernelConnectionStatus;
  isHealthy: boolean;
}

export type KernelHealthListener = (health: KernelHealth) => void;

export interface ExecuteResult {
  status: 'ok' | 'error' | 'abort';
  executionCount: number | null;
  outputs: CellOutput[];
}

export interface CellOutput {
  type:
    | 'stream'
    | 'execute_result'
    | 'display_data'
    | 'update_display_data'
    | 'error'
    | 'clear_output'
    | 'comm_open'
    | 'comm_msg';
  content: unknown;
  // Unique display ID for outputs that can be updated (tqdm, widgets, etc.)
  displayId?: string;
  // Comm ID for widget messages
  commId?: string;
}

export interface NotebookFile {
  name: string;
  path: string;
  cells: NotebookCell[];
  lastModified: string;
}

export interface TextFile {
  name: string;
  path: string;
  content: string;
  lastModified: string;
}

export interface DownloadedFile {
  name: string;
  path: string;
  mime: string;
  contentBase64: string;
}

export interface PreviewFile {
  name: string;
  path: string;
  mime: string;
  kind: PreviewFileKind;
  dataUrl: string | null;
  textContent: string | null;
}

export interface NotebookCell {
  id: string;
  type: 'code' | 'markdown';
  source: string;
  executionCount: number | null;
  outputs: CellOutput[];
}

export class JupyterClient {
  private _serverSettings: ServerConnection.ISettings;
  private _kernelManager: KernelManager | null = null;
  private _contentsManager: ContentsManager | null = null;
  private _kernel: IKernelConnection | null = null;
  private _widgetManager: WidgetManager | null = null;
  private _isConnected = false;
  private _isDisposed = false;
  private _healthListeners: Set<KernelHealthListener> = new Set();
  private _currentHealth: KernelHealth = {
    status: 'unknown',
    connectionStatus: 'disconnected',
    isHealthy: false,
  };
  // Tracks user-initiated teardowns (shutdownAllKernels, disconnect, force
  // restart) so we don't fire noisy Sentry alerts when the kernel dies as
  // a direct result of something the user asked for
  private _intentionalTeardown = false;

  constructor(baseUrl: string, token?: string) {
    this._serverSettings = ServerConnection.makeSettings({
      baseUrl,
      wsUrl: baseUrl.replace(/^http/, 'ws'),
      token: token ?? '',
      // Append token to WebSocket URLs for Envoy JWT validation
      appendToken: !!token,
    });
    // Note: Managers are lazily initialized in connect() to avoid auto-polling
  }

  get serverSettings(): ServerConnection.ISettings {
    return this._serverSettings;
  }

  get isConnected(): boolean {
    return this._isConnected && this._kernel !== null;
  }

  get kernel(): IKernelConnection | null {
    return this._kernel;
  }

  get widgetManager(): WidgetManager | null {
    return this._widgetManager;
  }

  /**
   * Get or lazily create the widget manager for rendering ipywidgets.
   *
   * Uses dynamic import() to load the widget infrastructure on demand,
   * avoiding eager loading of @jupyter-widgets packages (which pull in
   * heavy dependencies) at app startup.
   */
  async getWidgetManager(): Promise<WidgetManager | null> {
    if (this._widgetManager) return this._widgetManager;
    if (!this._kernel) return null;

    const { WidgetManager: WM } = await import('./WidgetManager');
    // Double-check kernel is still connected after async import
    if (!this._kernel) return null;
    this._widgetManager = new WM(this._kernel);
    return this._widgetManager;
  }

  get kernelHealth(): KernelHealth {
    return this._currentHealth;
  }

  // Subscribe to kernel health changes
  onKernelHealthChange(listener: KernelHealthListener): () => void {
    this._healthListeners.add(listener);
    // Immediately notify with current state
    listener(this._currentHealth);
    // Return unsubscribe function
    return () => this._healthListeners.delete(listener);
  }

  private _updateHealth(health: Partial<KernelHealth>): void {
    const newHealth = { ...this._currentHealth, ...health };
    // Compute isHealthy based on both statuses
    newHealth.isHealthy =
      newHealth.connectionStatus === 'connected' &&
      newHealth.status !== 'dead' &&
      newHealth.status !== 'unknown' &&
      newHealth.status !== 'terminating' &&
      newHealth.status !== 'restarting' &&
      newHealth.status !== 'autorestarting';
    this._currentHealth = newHealth;
    // Notify all listeners
    for (const listener of this._healthListeners) {
      listener(this._currentHealth);
    }
  }

  private _setupKernelListeners(): void {
    if (!this._kernel) return;

    // Reset the intentional teardown flag since we have a fresh kernel
    this._intentionalTeardown = false;

    // Listen to kernel execution status changes
    this._kernel.statusChanged.connect((_sender, status) => {
      const prevStatus = this._currentHealth.status;
      this._updateHealth({ status: status as KernelStatus });

      Sentry.addBreadcrumb({
        category: 'jupyter',
        message: `kernel status: ${status}`,
        level: status === 'dead' ? 'error' : 'info',
        data: { from: prevStatus, to: status },
      });

      // Classify the kernel death and report accordingly
      if (status === 'dead') {
        const classification = classifyKernelDeath(
          prevStatus,
          this._currentHealth.connectionStatus,
          this._intentionalTeardown
        );

        if (classification === 'error') {
          Sentry.captureException(new Error(`Kernel died unexpectedly (was ${prevStatus})`), {
            level: 'error',
            extra: {
              previousStatus: prevStatus,
              connectionStatus: this._currentHealth.connectionStatus,
            },
          });
        } else if (classification === 'breadcrumb') {
          Sentry.addBreadcrumb({
            category: 'jupyter',
            message: `kernel died (was ${prevStatus})`,
            level: 'warning',
            data: {
              previousStatus: prevStatus,
              connectionStatus: this._currentHealth.connectionStatus,
            },
          });
        }
      }
    });

    // Listen to WebSocket connection status changes
    this._kernel.connectionStatusChanged.connect((_sender, connectionStatus) => {
      this._updateHealth({ connectionStatus: connectionStatus as KernelConnectionStatus });

      Sentry.addBreadcrumb({
        category: 'jupyter',
        message: `kernel connection: ${connectionStatus}`,
        level: connectionStatus === 'disconnected' ? 'warning' : 'info',
      });
    });

    // Set initial status
    this._updateHealth({
      status: this._kernel.status as KernelStatus,
      connectionStatus: this._kernel.connectionStatus as KernelConnectionStatus,
    });
  }

  async connect(reconnectContext?: { attempt: number; disconnectedSinceMs: number }): Promise<void> {
    if (this._isDisposed) {
      throw new Error('Client has been disposed');
    }

    return await Sentry.startSpan({ name: 'Jupyter Connect', op: 'jupyter.connect' }, async (span) => {
      if (reconnectContext) {
        span.setAttribute('jupyter.reconnect_attempt', reconnectContext.attempt);
        span.setAttribute('jupyter.disconnected_since_ms', reconnectContext.disconnectedSinceMs);
      }
      // Lazily create managers to avoid polling until we're ready
      if (!this._kernelManager) {
        this._kernelManager = new KernelManager({
          serverSettings: this._serverSettings,
          // Use 'never' standby to prevent background polling when hidden
          standby: 'never',
        });
      }
      if (!this._contentsManager) {
        this._contentsManager = new ContentsManager({ serverSettings: this._serverSettings });
      }

      // Wait for kernel manager to be ready
      await this._kernelManager.ready;

      // Check for existing kernels
      const kernels = await KernelAPI.listRunning(this._serverSettings);
      let reusedExisting = false;
      if (kernels.length > 0) {
        // Connect to existing kernel
        this._kernel = await this._kernelManager.connectTo({ model: kernels[0] });
        reusedExisting = true;
        // Shut down any extra orphaned kernels from previous sessions
        for (const k of kernels.slice(1)) {
          KernelAPI.shutdownKernel(k.id, this._serverSettings).catch(() => {});
        }
      } else {
        // Start new kernel using antioch-sim kernelspec
        this._kernel = await this._kernelManager.startNew({ name: 'antioch-sim' });
      }

      span.setAttribute('jupyter.reused_existing', reusedExisting);
      span.setAttribute('jupyter.kernel_name', 'antioch-sim');

      // Set up status listeners
      this._setupKernelListeners();

      this._isConnected = true;

      // Initialize widget manager before connect resolves so the comm target
      // is registered before any cells execute -- prevents dropped comm_open
      // messages that cause ipywidgets (tqdm bars, etc.) to fail silently
      await this.getWidgetManager().catch((error) => {
        Sentry.captureException(error, {
          level: 'warning',
          tags: { module: 'jupyter', operation: 'init_widget_manager' },
        });
      });
    });
  }

  /**
   * Connect to a specific existing kernel by ID.
   *
   * Unlike connect(), this does not kill other kernels or auto-discover.
   * Used by the KernelPool to attach to specific kernels. The pool has
   * already polled `listRunning()` and knows the kernel name, so we use
   * a single targeted GET `/jupyter/api/kernels/{id}` (`getKernelModel`)
   * instead of refetching the full list -- listing here was the root of
   * the N+1 burst flagged by Sentry on `/workspace` mount.
   *
   * If the kernel has died between the pool's last refresh and this
   * attach attempt, throws `KernelNotFoundError` so the caller can
   * self-heal and surface a user-friendly message.
   */
  async connectToKernel(kernelId: string, kernelName: string = 'antioch-sim'): Promise<void> {
    if (this._isDisposed) {
      throw new Error('Client has been disposed');
    }

    if (!this._kernelManager) {
      this._kernelManager = new KernelManager({
        serverSettings: this._serverSettings,
        standby: 'never',
      });
    }
    if (!this._contentsManager) {
      this._contentsManager = new ContentsManager({ serverSettings: this._serverSettings });
    }

    await this._kernelManager.ready;

    // Targeted existence check; returns undefined on 404 without throwing
    const model = await KernelAPI.getKernelModel(kernelId, this._serverSettings);
    if (!model) {
      throw new KernelNotFoundError(kernelId);
    }

    this._kernel = await this._kernelManager.connectTo({ model: { id: model.id, name: model.name ?? kernelName } });
    this._setupKernelListeners();
    this._isConnected = true;
  }

  /**
   * Start a new kernel with the given kernelspec name.
   *
   * Unlike connect(), this always starts a fresh kernel and does not
   * kill other running kernels. Used by the KernelPool.
   */
  async startNewKernel(kernelspec: string = 'antioch-sim'): Promise<string> {
    if (this._isDisposed) {
      throw new Error('Client has been disposed');
    }

    if (!this._kernelManager) {
      this._kernelManager = new KernelManager({
        serverSettings: this._serverSettings,
        standby: 'never',
      });
    }
    if (!this._contentsManager) {
      this._contentsManager = new ContentsManager({ serverSettings: this._serverSettings });
    }

    await this._kernelManager.ready;
    this._kernel = await this._kernelManager.startNew({ name: kernelspec });
    this._setupKernelListeners();
    this._isConnected = true;

    return this._kernel.id;
  }

  /**
   * Disconnect from the kernel and shut it down on the server.
   *
   * Use this for intentional disconnects (switching workspaces, explicit
   * disconnect button) where the kernel process should be terminated.
   */
  async disconnect(): Promise<void> {
    this._intentionalTeardown = true;
    // Shut down kernel on the server (kills the process)
    if (this._kernel) {
      try {
        await KernelAPI.shutdownKernel(this._kernel.id, this._serverSettings);
      } catch {
        // Best-effort: server may be unreachable
      }
    }

    // Then clean up all local resources
    this.detach();
  }

  /**
   * Detach from the kernel without killing it on the server.
   *
   * Closes the local WebSocket connection and disposes managers, but leaves
   * the kernel process running on the workspace. Use this for page refreshes
   * and component unmounts where we want to reconnect to the same kernel.
   */
  detach(): void {
    // Dispose widget manager
    if (this._widgetManager) {
      this._widgetManager.disconnect();
      this._widgetManager = null;
    }

    // Close local WebSocket connection without killing the server-side kernel
    if (this._kernel) {
      this._kernel.dispose();
      this._kernel = null;
    }

    // Dispose managers to stop polling
    if (this._kernelManager) {
      this._kernelManager.dispose();
      this._kernelManager = null;
    }
    if (this._contentsManager) {
      this._contentsManager.dispose();
      this._contentsManager = null;
    }

    // Reset health status
    this._updateHealth({
      status: 'unknown',
      connectionStatus: 'disconnected',
    });

    this._isConnected = false;
    this._isDisposed = true;
  }

  /**
   * Change the kernel's working directory to the notebook's parent directory.
   *
   * This replicates what JupyterLab does via the Sessions API: when a notebook
   * is opened, the kernel's cwd is set to the notebook's directory so that
   * relative imports and file references work correctly.
   *
   * The notebook path is relative to the Jupyter server's root_dir (e.g. "ring/ring.ipynb").
   * We discover root_dir by reading the server's runtime file (jpserver-*.json),
   * which works in both the container (root_dir=/root) and local dev (root_dir=arbitrary).
   */
  async changeNotebookDirectory(notebookPath: string): Promise<void> {
    if (!this._kernel) {
      console.warn('[jupyter] changeNotebookDirectory: no kernel, skipping');
      return;
    }

    const notebookDir = notebookPath.includes('/') ? notebookPath.substring(0, notebookPath.lastIndexOf('/')) : '';

    const code = `def _change_nb_dir():
    import json, os, sys, glob
    try:
        from jupyter_core.paths import jupyter_runtime_dir
        runtime_dir = jupyter_runtime_dir()
    except ImportError:
        runtime_dir = os.path.join(os.path.expanduser("~"), ".local", "share", "jupyter", "runtime")
    server_root = os.path.expanduser("~")
    runtime_files = sorted(glob.glob(os.path.join(runtime_dir, "jpserver-*.json")), key=os.path.getmtime, reverse=True)
    for rf in runtime_files:
        try:
            with open(rf) as f:
                server_root = json.load(f).get("root_dir", server_root)
                break
        except Exception:
            pass
    nb_dir = os.path.normpath(os.path.join(server_root, ${JSON.stringify(notebookDir)}))
    if not os.path.isdir(nb_dir):
        print(f"[changeNotebookDirectory] WARNING: directory does not exist: {nb_dir}", file=sys.stderr)
        print(f"  runtime_dir={runtime_dir}, files={runtime_files}, server_root={server_root}", file=sys.stderr)
        return
    os.chdir(nb_dir)
    if nb_dir not in sys.path:
        sys.path.insert(0, nb_dir)
_change_nb_dir()
del _change_nb_dir`;

    const result = await this.executeCode(code, true, false);
    if (result.status !== 'ok') {
      console.warn('[jupyter] changeNotebookDirectory failed', {
        notebookPath,
        notebookDir,
        status: result.status,
        outputs: result.outputs,
      });
    }
  }

  async executeCode(
    code: string,
    silent = false,
    storeHistory = true,
    onOutput?: (output: CellOutput) => void
  ): Promise<ExecuteResult> {
    if (!this._kernel) {
      throw new Error('Not connected to kernel');
    }

    return await Sentry.startSpan({ name: 'Jupyter Execute', op: 'jupyter.execute' }, async (span) => {
      span.setAttribute('jupyter.code_length', code.length);
      span.setAttribute('jupyter.silent', silent);
      span.setAttribute('jupyter.store_history', storeHistory);

      const outputs: CellOutput[] = [];
      let executionCount: number | null = null;
      let status: 'ok' | 'error' | 'abort' = 'ok';

      // requestExecute throws synchronously if the kernel is dead (e.g. after
      // a shutdown or crash). This is expected -- return a clean abort.
      let future: any;
      try {
        future = this._kernel!.requestExecute({
          code,
          silent,
          store_history: storeHistory,
        });
      } catch (err) {
        if (err instanceof Error && err.message.includes('Kernel is dead')) {
          return { status: 'abort', executionCount: null, outputs };
        }
        throw err;
      }

      // Handle IOPub messages for outputs - stream in real-time if callback provided
      future.onIOPub = (msg: IIOPubMessage) => {
        const msgType = msg.header.msg_type;
        const content = msg.content as Record<string, unknown>;

        let output: CellOutput | null = null;

        // Extract display_id from transient data if present (used by tqdm, widgets)
        const transient = content.transient as Record<string, unknown> | undefined;
        const displayId = transient?.display_id as string | undefined;

        switch (msgType) {
          case 'stream':
            output = { type: 'stream', content };
            break;
          case 'execute_result':
            output = { type: 'execute_result', content, displayId };
            break;
          case 'display_data':
            output = { type: 'display_data', content, displayId };
            break;
          case 'update_display_data':
            // Update existing display output with matching displayId
            output = { type: 'update_display_data', content, displayId };
            break;
          case 'clear_output':
            // Signal to clear previous outputs (used by tqdm, widgets)
            output = { type: 'clear_output', content };
            break;
          case 'comm_open': {
            // Widget communication channel opened
            const commId = content.comm_id as string | undefined;
            output = { type: 'comm_open', content, commId };
            break;
          }
          case 'comm_msg': {
            // Widget state update
            const msgCommId = content.comm_id as string | undefined;
            output = { type: 'comm_msg', content, commId: msgCommId };
            break;
          }
          case 'error':
            output = { type: 'error', content };
            break;
        }

        if (output) {
          outputs.push(output);
          // Stream output in real-time if callback provided
          if (onOutput) {
            onOutput(output);
          }
        }
      };

      // Wait for execution to complete. When the kernel is disposed (shutdown,
      // restart, kill-all), @jupyterlab/services rejects all pending futures
      // with "Canceled future for execute_request message before replies were
      // done". This is expected -- return a clean abort instead of throwing.
      let reply: any;
      try {
        reply = await future.done;
      } catch (err) {
        if (err instanceof Error && err.message.includes('Canceled future')) {
          return { status: 'abort', executionCount: null, outputs };
        }
        throw err;
      }
      const replyContent = (reply as IExecuteReplyMsg).content;
      status = replyContent.status as 'ok' | 'error' | 'abort';
      executionCount = replyContent.execution_count ?? null;

      span.setAttribute('jupyter.status', status);
      span.setAttribute('jupyter.output_count', outputs.length);
      if (executionCount !== null) span.setAttribute('jupyter.execution_count', executionCount);

      return { status, executionCount, outputs };
    });
  }

  /**
   * Subscribe to the kernel's IOPub channel for real-time output.
   *
   * This is used after a page refresh to reattach to an execution that is
   * still running on the server. Unlike executeCode (which ties a callback
   * to a specific request future), this catches ALL IOPub messages —
   * including output from an execute request we no longer hold a reference to.
   *
   * Returns an unsubscribe function.
   */
  subscribeToIOPub(onOutput: (output: CellOutput) => void): () => void {
    if (!this._kernel) return () => {};

    const handler = (_sender: unknown, msg: IIOPubMessage) => {
      const msgType = msg.header.msg_type;
      const content = msg.content as Record<string, unknown>;
      let output: CellOutput | null = null;

      const transient = content.transient as Record<string, unknown> | undefined;
      const displayId = transient?.display_id as string | undefined;

      switch (msgType) {
        case 'stream':
          output = { type: 'stream', content };
          break;
        case 'execute_result':
          output = { type: 'execute_result', content, displayId };
          break;
        case 'display_data':
          output = { type: 'display_data', content, displayId };
          break;
        case 'update_display_data':
          output = { type: 'update_display_data', content, displayId };
          break;
        case 'clear_output':
          output = { type: 'clear_output', content };
          break;
        case 'error':
          output = { type: 'error', content };
          break;
      }

      if (output) {
        onOutput(output);
      }
    };

    this._kernel.iopubMessage.connect(handler);
    return () => {
      this._kernel?.iopubMessage.disconnect(handler);
    };
  }

  /**
   * Interrupt the running kernel.
   *
   * The kernelspec uses `interrupt_mode: message`, so this sends an
   * `interrupt_request` on the control channel and ipykernel raises
   * SIGINT in the kernel process. Thin wrapper over
   * @jupyterlab/services — callers fire it without blocking the UI and
   * decide how to report failures.
   *
   * :raises Error: If no kernel is attached.
   */
  async interruptKernel(): Promise<void> {
    if (!this._kernel) {
      throw new Error('Not connected to kernel');
    }
    await this._kernel.interrupt();
  }

  /**
   * Restart the kernel, either gracefully or by force.
   *
   * Graceful (default): asks the Jupyter server to restart the kernel
   * process in-place (SIGTERM → wait → SIGKILL → start). This reuses
   * the same kernel ID and WebSocket but can take 5-10+ seconds when
   * Isaac Sim ignores signals.
   *
   * Force: immediately disposes the local kernel reference, fires off
   * the server-side kill without waiting, and starts a brand-new
   * kernel. Much faster (~1-2s) at the cost of a new kernel ID.
   *
   * :param force: Skip the graceful restart attempt and go straight to
   *   dispose + start new. Use this for user-initiated stop operations
   *   where speed matters.
   */
  async restartKernel(force = false): Promise<void> {
    if (!this._kernelManager) {
      throw new Error('Not connected');
    }

    const kernelManager = this._kernelManager;
    return await Sentry.startSpan(
      { name: 'Jupyter Restart', op: 'jupyter.restart', attributes: { 'jupyter.force': force } },
      async (span) => {
        // Kernel restart is user-initiated -- suppress death alerts for the old kernel
        this._intentionalTeardown = true;

        // Clear widget manager - kernel restart invalidates all widget state
        if (this._widgetManager) {
          this._widgetManager.disconnect();
          this._widgetManager = null;
        }

        // If we have a kernel, try to restart it with a timeout. The Jupyter
        // server restart blocks until the old process dies (SIGTERM → wait →
        // SIGKILL), which can take 10+ seconds if Isaac Sim ignores signals.
        if (this._kernel) {
          const oldId = this._kernel.id;
          if (!force) {
            // The graceful restart calls kernel.restart() -> kernel.reconnect()
            // internally. If the timeout wins the race below, the abandoned
            // restart() promise still has a pending reconnect inside it, and
            // when we later call kernel.dispose() the library rejects that
            // pending reconnect with "Kernel connection disconnected" -- which
            // becomes an unhandled rejection. Attaching a no-op catch ensures
            // the rejection is observed regardless of who wins the race.
            try {
              const restartPromise = this._kernel.restart();
              restartPromise.catch(() => {
                // Intentionally swallow: any rejection thrown after the timeout
                // wins the Promise.race is handled by the force-restart fallback
                // below; rejections that happen before the timeout are surfaced
                // by the await on Promise.race itself.
              });
              await Promise.race([
                restartPromise,
                new Promise<never>((_, reject) => setTimeout(() => reject(new Error('restart timeout')), 5000)),
              ]);
              this._setupKernelListeners();
              this._isConnected = true;
              await this.getWidgetManager().catch((error) => {
                Sentry.captureException(error, {
                  level: 'warning',
                  tags: { module: 'jupyter', operation: 'init_widget_manager' },
                });
              });
              span.setAttribute('jupyter.graceful_succeeded', true);
              return;
            } catch {
              // Restart timed out or the kernel is dead/stale -- fall through
              // to the dispose + start-new path below
              span.setAttribute('jupyter.graceful_succeeded', false);
              Sentry.addBreadcrumb({
                category: 'jupyter',
                message: 'graceful kernel restart timed out, forcing restart',
                level: 'warning',
              });
            }
          }
          // Signal that the kernel is going away so consumers (e.g. the stream
          // component) can immediately treat the simulation as dead rather than
          // waiting for the /sim/status poll to time out
          Sentry.addBreadcrumb({
            category: 'jupyter',
            message: 'force kernel restart initiated',
          });
          this._updateHealth({ status: 'restarting', connectionStatus: 'disconnected' });
          // Dispose local reference and fire-and-forget the server-side kill
          // so we don't block on it. The server will eventually SIGKILL the process.
          this._kernel.dispose();
          this._kernel = null;
          KernelAPI.shutdownKernel(oldId, this._serverSettings).catch(() => {});
        }

        // Start a fresh antioch-sim kernel (independent of old process dying)
        this._kernel = await kernelManager.startNew({ name: 'antioch-sim' });
        this._isConnected = true;
        this._setupKernelListeners();
        await this.getWidgetManager().catch((error) => {
          Sentry.captureException(error, {
            level: 'warning',
            tags: { module: 'jupyter', operation: 'init_widget_manager' },
          });
        });
      }
    );
  }

  /**
   * Wait for the kernel to reach idle status.
   *
   * Use after restartKernel to ensure the new kernel is fully initialized
   * and ready to accept execute requests. Resolves immediately if the
   * kernel is already idle.
   *
   * :param timeoutMs: Maximum time to wait before rejecting.
   */
  async waitForIdle(timeoutMs = 15_000): Promise<void> {
    if (this._currentHealth.status === 'idle') return;
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        unsub();
        reject(new Error('Timed out waiting for kernel to become idle'));
      }, timeoutMs);
      const unsub = this.onKernelHealthChange((health) => {
        if (health.status === 'idle') {
          clearTimeout(timer);
          unsub();
          resolve();
        }
      });
    });
  }

  /**
   * Shut down all running kernels on the Jupyter server.
   *
   * Lists all active kernels via the REST API and terminates them.
   * Also cleans up local kernel and widget manager references.
   *
   * :return: The number of kernels that were shut down.
   */
  async shutdownAllKernels(): Promise<number> {
    this._intentionalTeardown = true;
    const running = await KernelAPI.listRunning(this._serverSettings);
    if (running.length === 0) return 0;

    // Fire-and-forget the shutdown requests -- don't block waiting for
    // stubborn processes (Isaac Sim) to die. The server will eventually
    // SIGKILL them. We just need the server to acknowledge the request.
    await Promise.all(
      running.map((k) =>
        Promise.race([
          KernelAPI.shutdownKernel(k.id, this._serverSettings),
          new Promise<void>((resolve) => setTimeout(resolve, 3000)),
        ])
      )
    );

    // Clean up local references since our kernel is now dead
    if (this._widgetManager) {
      this._widgetManager.disconnect();
      this._widgetManager = null;
    }
    if (this._kernel) {
      this._kernel.dispose();
      this._kernel = null;
    }
    this._isConnected = false;
    this._updateHealth({ status: 'unknown', connectionStatus: 'disconnected' });

    return running.length;
  }

  async listNotebooks(path = ''): Promise<{ name: string; path: string }[]> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const contents = await this._contentsManager.get(path, { content: true });
    if (contents.type !== 'directory') {
      return [];
    }

    const notebooks: { name: string; path: string }[] = [];
    const items = contents.content as Array<{ type: string; name: string; path: string }>;

    for (const item of items) {
      if (item.type === 'notebook') {
        notebooks.push({ name: item.name, path: item.path });
      }
    }

    return notebooks;
  }

  /**
   * List contents of a directory.
   * Returns files, directories, and notebooks with metadata.
   */
  async listDirectory(path = ''): Promise<FileItem[]> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const contents = await this._contentsManager.get(path, { content: true });
    if (contents.type !== 'directory') {
      return [];
    }

    const items = contents.content as Array<{
      type: string;
      name: string;
      path: string;
      last_modified: string;
      size?: number;
    }>;

    return items.map((item) => ({
      name: item.name,
      path: item.path,
      type: item.type as 'file' | 'directory' | 'notebook',
      lastModified: item.last_modified,
      size: item.size,
    }));
  }

  /**
   * Check whether a file or directory exists at the given path.
   */
  async fileExists(path: string): Promise<boolean> {
    if (!this._contentsManager) return false;
    try {
      await this._contentsManager.get(path, { content: false });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Create a new notebook at the specified path.
   *
   * If a name is provided, the notebook is created with that exact name.
   * Throws if a file already exists at the target path to prevent data loss.
   * Otherwise, a Jupyter-generated untitled name is used.
   */
  async createNotebook(directory = '', name?: string): Promise<{ name: string; path: string }> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    if (name) {
      const fileName = name.endsWith('.ipynb') ? name : `${name}.ipynb`;
      const filePath = directory ? `${directory}/${fileName}` : fileName;
      if (await this.fileExists(filePath)) {
        throw new Error(`File '${fileName}' already exists`);
      }
      const result = await this._contentsManager.save(filePath, {
        type: 'notebook',
        content: { cells: [], metadata: {}, nbformat: 4, nbformat_minor: 5 },
      });
      return { name: result.name, path: result.path };
    }
    const result = await this._contentsManager.newUntitled({
      path: directory,
      type: 'notebook',
      ext: '.ipynb',
    });
    return { name: result.name, path: result.path };
  }

  /**
   * Create a new Python file at the specified path.
   *
   * If a name is provided, the file is created with that exact name.
   * Throws if a file already exists at the target path to prevent data loss.
   * Otherwise, a Jupyter-generated untitled name is used.
   */
  async createPythonFile(directory = '', name?: string): Promise<{ name: string; path: string }> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    if (name) {
      const fileName = name.endsWith('.py') ? name : `${name}.py`;
      const filePath = directory ? `${directory}/${fileName}` : fileName;
      if (await this.fileExists(filePath)) {
        throw new Error(`File '${fileName}' already exists`);
      }
      const result = await this._contentsManager.save(filePath, {
        type: 'file',
        format: 'text',
        content: '',
      });
      return { name: result.name, path: result.path };
    }
    const result = await this._contentsManager.newUntitled({
      path: directory,
      type: 'file',
      ext: '.py',
    });
    return { name: result.name, path: result.path };
  }

  /**
   * Create a new empty text file at the specified path.
   *
   * The provided name is used as-is so callers can create any
   * workspace file type, such as ``.json``, ``.yaml``, ``.urdf``,
   * or extensionless helper files.
   */
  async createFile(directory = '', name: string): Promise<{ name: string; path: string }> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const filePath = directory ? `${directory}/${name}` : name;
    if (await this.fileExists(filePath)) {
      throw new Error(`File '${name}' already exists`);
    }
    const result = await this._contentsManager.save(filePath, {
      type: 'file',
      format: 'text',
      content: '',
    });
    return { name: result.name, path: result.path };
  }

  /**
   * Create a new folder under the specified directory.
   */
  async createDirectory(directory = '', name: string): Promise<{ name: string; path: string }> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const directoryPath = directory ? `${directory}/${name}` : name;
    if (await this.fileExists(directoryPath)) {
      throw new Error(`Folder '${name}' already exists`);
    }
    const created = await this._contentsManager.newUntitled({
      path: directory,
      type: 'directory',
    });
    try {
      const renamed = await this._contentsManager.rename(created.path, directoryPath);
      return { name: renamed.name, path: renamed.path };
    } catch (err) {
      await this._contentsManager.delete(created.path).catch(() => undefined);
      throw err;
    }
  }

  /**
   * Write text content to a file via the Jupyter ContentsManager REST API.
   *
   * Uses the Jupyter server's file tree, not the kernel's filesystem.
   * This ensures consistency between file writes and kernel execution
   * since the ContentsManager root is the same as the notebook root.
   * Intermediate directories are created automatically by the API.
   */
  async saveFile(path: string, content: string): Promise<void> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    await this._contentsManager.save(path, {
      type: 'file',
      format: 'text',
      content,
    });
  }

  /**
   * Read text content from a file via the Jupyter ContentsManager REST API.
   */
  async readFile(path: string): Promise<string> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const result = await this._contentsManager.get(path, { content: true });
    return String(result.content);
  }

  /**
   * Delete a file or notebook.
   */
  async deleteFile(path: string): Promise<void> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    await this._contentsManager.delete(path);
  }

  /**
   * Rename a file or notebook.
   */
  async renameFile(oldPath: string, newPath: string): Promise<void> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    await this._contentsManager.rename(oldPath, newPath);
  }

  async loadNotebook(path: string): Promise<NotebookFile> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const contents = await this._contentsManager.get(path, { content: true });
    if (contents.type !== 'notebook') {
      throw new Error(`Path ${path} is not a notebook`);
    }

    const nbContent = contents.content as {
      cells: Array<{
        id?: string;
        cell_type: string;
        source: string | string[];
        execution_count?: number | null;
        outputs?: unknown[];
      }>;
    };

    const cells: NotebookCell[] = nbContent.cells.map((cell, idx) => ({
      id: cell.id ?? `cell-${idx}`,
      type: cell.cell_type === 'code' ? 'code' : 'markdown',
      source: Array.isArray(cell.source) ? cell.source.join('') : cell.source,
      executionCount: cell.execution_count ?? null,
      outputs: (cell.outputs ?? []).map((output) => this._parseOutput(output)),
    }));

    return {
      name: contents.name,
      path: contents.path,
      cells,
      lastModified: contents.last_modified,
    };
  }

  async saveNotebook(path: string, cells: NotebookCell[]): Promise<void> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const nbContent = {
      cells: cells.map((cell) => ({
        id: cell.id,
        cell_type: cell.type,
        source: cell.source,
        metadata: {},
        execution_count: cell.type === 'code' ? cell.executionCount : undefined,
        outputs: cell.type === 'code' ? cell.outputs.map((o) => this._serializeOutput(o)) : undefined,
      })),
      metadata: {
        kernelspec: {
          display_name: 'antioch-sim',
          language: 'python',
          name: 'antioch-sim',
        },
        language_info: {
          name: 'python',
        },
      },
      nbformat: 4,
      nbformat_minor: 5,
    };

    await this._contentsManager.save(path, {
      type: 'notebook',
      content: nbContent,
    });
  }

  /**
   * Best-effort save using fetch with keepalive: true.
   *
   * Designed for use in beforeunload handlers where the page may unload
   * before the request completes. The keepalive flag tells the browser to
   * finish the request even after the page is gone.
   */
  saveNotebookKeepalive(path: string, cells: NotebookCell[]): void {
    const { baseUrl, token } = this._serverSettings;
    const url = new URL(`api/contents/${path}`, baseUrl).toString();
    const nbContent = {
      cells: cells.map((cell) => ({
        id: cell.id,
        cell_type: cell.type,
        source: cell.source,
        metadata: {},
        execution_count: cell.type === 'code' ? cell.executionCount : undefined,
        outputs: cell.type === 'code' ? cell.outputs.map((o) => this._serializeOutput(o)) : undefined,
      })),
      metadata: {
        kernelspec: { display_name: 'antioch-sim', language: 'python', name: 'antioch-sim' },
        language_info: { name: 'python' },
      },
      nbformat: 4,
      nbformat_minor: 5,
    };

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `token ${token}`;
    }

    try {
      fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ type: 'notebook', content: nbContent }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Best-effort only
    }
  }

  /**
   * Load a text file from the Jupyter contents API.
   *
   * Reads the file at the given path and returns its content as a string.
   * Throws if the path points to a directory or notebook.
   *
   * :param path: Relative path to the file within the Jupyter root.
   * :return: The loaded text file with name, path, and content.
   */
  async loadTextFile(path: string): Promise<TextFile> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const contents = await this._contentsManager.get(path, { content: true, type: 'file' });
    if (contents.type === 'directory') {
      throw new Error(`Path '${path}' is a directory, not a file`);
    }
    return {
      name: contents.name,
      path: contents.path,
      content: typeof contents.content === 'string' ? contents.content : '',
      lastModified: contents.last_modified,
    };
  }

  /**
   * Load a previewable file from the Jupyter contents API.
   *
   * Binary artifacts are requested as base64 so the browser can render
   * them without a separate unauthenticated URL. HTML reports are also
   * decoded to text for sandboxed iframe rendering.
   */
  async loadPreviewFile(path: string): Promise<PreviewFile> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    const kind = previewFileKind(path);
    const mime = mimeTypeForPath(path);
    const contents = await this._contentsManager.get(path, { content: true, type: 'file', format: 'base64' });
    if (contents.type === 'directory') {
      throw new Error(`Path '${path}' is a directory, not a file`);
    }

    const encoded = typeof contents.content === 'string' ? contents.content : '';
    const textContent = kind === 'html' && encoded ? decodeBase64Text(encoded) : null;
    return {
      name: contents.name,
      path: contents.path,
      mime,
      kind,
      dataUrl: encoded ? `data:${mime};base64,${encoded}` : null,
      textContent,
    };
  }

  /**
   * Save a text file via the Jupyter contents API.
   *
   * Writes the given string content to the file at the specified path.
   * Creates the file if it does not exist.
   *
   * :param path: Relative path to the file within the Jupyter root.
   * :param content: The text content to write.
   */
  async saveTextFile(path: string, content: string): Promise<void> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    await this._contentsManager.save(path, {
      type: 'file',
      format: 'text',
      content,
    });
  }

  /**
   * Get the last_modified timestamp of a file without fetching content.
   */
  async getFileMtime(path: string): Promise<string | null> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }
    try {
      const contents = await this._contentsManager.get(path, { content: false });
      return contents.last_modified;
    } catch {
      return null;
    }
  }

  /**
   * Upload a browser File into the Jupyter contents tree.
   */
  async uploadFile(path: string, file: File): Promise<{ name: string; path: string }> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }

    if (file.name.toLowerCase().endsWith('.ipynb')) {
      const text = await file.text();
      const notebook = JSON.parse(text) as unknown;
      const result = await this._contentsManager.save(path, {
        type: 'notebook',
        content: notebook,
      });
      return { name: result.name, path: result.path };
    }

    const content = arrayBufferToBase64(await file.arrayBuffer());
    const result = await this._contentsManager.save(path, {
      type: 'file',
      format: 'base64',
      content,
    });
    return { name: result.name, path: result.path };
  }

  /**
   * Download a file from the Jupyter contents tree as base64 data.
   */
  async downloadFile(path: string): Promise<DownloadedFile> {
    if (!this._contentsManager) {
      throw new Error('Not connected');
    }

    if (path.toLowerCase().endsWith('.ipynb')) {
      const contents = await this._contentsManager.get(path, { content: true, type: 'notebook' });
      if (contents.type === 'directory') {
        throw new Error(`Path '${path}' is a directory, not a file`);
      }
      return {
        name: contents.name,
        path: contents.path,
        mime: mimeTypeForPath(path),
        contentBase64: textToBase64(JSON.stringify(contents.content, null, 2)),
      };
    }

    const contents = await this._contentsManager.get(path, { content: true, type: 'file', format: 'base64' });
    if (contents.type === 'directory') {
      throw new Error(`Path '${path}' is a directory, not a file`);
    }

    return {
      name: contents.name,
      path: contents.path,
      mime: mimeTypeForPath(path),
      contentBase64: typeof contents.content === 'string' ? contents.content : '',
    };
  }

  private _parseOutput(output: unknown): CellOutput {
    const o = output as { output_type: string; transient?: { display_id?: string } };
    const displayId = o.transient?.display_id;
    switch (o.output_type) {
      case 'stream':
        return { type: 'stream', content: output };
      case 'execute_result':
        return { type: 'execute_result', content: output, displayId };
      case 'display_data':
        return { type: 'display_data', content: output, displayId };
      case 'update_display_data':
        return { type: 'update_display_data', content: output, displayId };
      case 'clear_output':
        return { type: 'clear_output', content: output };
      case 'error':
        return { type: 'error', content: output };
      default:
        return { type: 'stream', content: output };
    }
  }

  private _serializeOutput(output: CellOutput): unknown {
    return {
      output_type: output.type,
      ...(output.content as object),
    };
  }

  /**
   * Fetch current variables from the kernel.
   * Returns an array of variable info objects.
   */
  async getVariables(): Promise<VariableInfo[]> {
    if (!this._kernel) {
      return [];
    }

    // Python code to introspect variables with detailed previews
    const code = `
import json as _json
import builtins as _builtins
def _get_var_info():
    _skip = {'In', 'Out', 'get_ipython', 'exit', 'quit', '_', '__', '___', '_i', '_ii', '_iii', '_oh', '_dh', '_sh'}
    _builtin_names = set(dir(_builtins))
    _result = []
    for _name, _val in list(globals().items()):
        if _name.startswith('_') or _name in _skip or _name in _builtin_names:
            continue
        try:
            _type = type(_val).__name__
            _shape = ''
            _preview = ''
            # Get shape for arrays and collections
            if hasattr(_val, 'shape'):
                _shape = str(_val.shape)
            elif hasattr(_val, '__len__') and not isinstance(_val, str):
                try:
                    _shape = f'len={len(_val)}'
                except:
                    pass
            # Get preview - more detailed for different types
            if isinstance(_val, (int, float, bool)):
                _preview = repr(_val)
            elif isinstance(_val, str):
                if len(_val) > 200:
                    _preview = repr(_val[:200]) + '...'
                else:
                    _preview = repr(_val)
            elif isinstance(_val, (list, tuple)):
                try:
                    _repr = repr(_val)
                    _preview = _repr[:300] + ('...' if len(_repr) > 300 else '')
                except:
                    _preview = f'{_type} with {len(_val)} items'
            elif isinstance(_val, dict):
                try:
                    _repr = repr(_val)
                    _preview = _repr[:300] + ('...' if len(_repr) > 300 else '')
                except:
                    _preview = f'dict with {len(_val)} keys'
            elif hasattr(_val, 'shape') and hasattr(_val, 'dtype'):
                # numpy/tensor arrays
                _preview = f'{_type} {_val.shape} dtype={_val.dtype}'
            elif hasattr(_val, '__repr__'):
                try:
                    _repr = repr(_val)
                    _preview = _repr[:300] + ('...' if len(_repr) > 300 else '')
                except:
                    _preview = _type
            else:
                _preview = _type
            _result.append({'name': _name, 'type': _type, 'shape': _shape, 'preview': _preview})
        except:
            pass
    return _result
print(_json.dumps(_get_var_info()))
del _get_var_info
`;

    try {
      const result = await this.executeCode(code, true, false);
      const streamOutput = result.outputs.find((o) => o.type === 'stream');
      if (streamOutput) {
        const text = (streamOutput.content as { text?: string }).text ?? '';
        return JSON.parse(text.trim()) as VariableInfo[];
      }
    } catch {
      // Variable introspection can fail for various reasons - fail silently
    }

    return [];
  }

  /**
   * Evaluate an expression and return its repr and type.
   *
   * Executes code silently to get the current value of a variable or expression.
   * Useful for showing variable values in hover tooltips.
   *
   * :param expression: The expression to evaluate (typically a variable name).
   * :param maxLength: Maximum length of the repr output (default 500).
   * :return: Evaluation result with value and type.
   */
  async evaluate(expression: string, maxLength = 500): Promise<EvaluateResult> {
    if (!this._kernel || !this._isConnected) {
      return { status: 'error', value: null, type: null };
    }

    // Python code to safely evaluate expression and get repr + type
    const code = `
try:
    _expr_val = ${expression}
    _expr_type = type(_expr_val).__name__
    _expr_repr = repr(_expr_val)
    if len(_expr_repr) > ${maxLength}:
        _expr_repr = _expr_repr[:${maxLength}] + '...'
    print(f"__EVAL_TYPE__:{_expr_type}")
    print(f"__EVAL_VALUE__:{_expr_repr}")
    del _expr_val, _expr_type, _expr_repr
except:
    print("__EVAL_ERROR__")
`;

    try {
      const result = await this.executeCode(code, true, false);
      // Concatenate all stream outputs (multiple print() calls may create separate outputs)
      const streamOutputs = result.outputs.filter((o) => o.type === 'stream');
      const text = streamOutputs.map((o) => (o.content as { text?: string }).text ?? '').join('');

      if (text) {
        if (text.includes('__EVAL_ERROR__')) {
          return { status: 'error', value: null, type: null };
        }
        const typeMatch = text.match(/__EVAL_TYPE__:(.+)/);
        const valueMatch = text.match(/__EVAL_VALUE__:([\s\S]+)/);
        if (typeMatch && valueMatch) {
          return {
            status: 'ok',
            type: typeMatch[1].trim(),
            value: valueMatch[1].trim(),
          };
        }
      }
    } catch {
      // Evaluation can fail for various reasons - fail silently
    }

    return { status: 'error', value: null, type: null };
  }

  /**
   * Request inspection/documentation for code at cursor position.
   *
   * Uses the Jupyter kernel's inspect_request to get docstrings,
   * type information, and signatures for the symbol at the cursor.
   *
   * :param code: The code string to inspect.
   * :param cursorPos: Cursor position (character offset) in the code.
   * :param detailLevel: 0 for signature + short docstring, 1 for full docstring.
   * :return: Inspection result with documentation data.
   */
  async inspect(code: string, cursorPos: number, detailLevel: 0 | 1 = 0): Promise<InspectResult> {
    if (!this._kernel || !this._isConnected) {
      return { status: 'error', found: false, data: {} };
    }

    try {
      const reply = await this._kernel.requestInspect({
        code,
        cursor_pos: cursorPos,
        detail_level: detailLevel,
      });
      const content = reply.content as {
        status: 'ok' | 'error';
        found?: boolean;
        data?: { 'text/plain'?: string; 'text/html'?: string };
      };

      if (content.status === 'ok') {
        // Check if data has content - some kernels don't set 'found' but do provide data
        const hasData = content.data !== undefined && Object.keys(content.data).length > 0;
        return {
          status: 'ok',
          found: content.found ?? hasData,
          data: content.data ?? {},
        };
      }

      return { status: 'error', found: false, data: {} };
    } catch {
      return { status: 'error', found: false, data: {} };
    }
  }
}

export interface VariableInfo {
  name: string;
  type: string;
  shape: string;
  preview: string;
}

export interface FileItem {
  name: string;
  path: string;
  type: 'file' | 'directory' | 'notebook';
  lastModified: string;
  size?: number;
}

function decodeBase64Text(encoded: string): string {
  const binary = globalThis.atob(encoded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function textToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  return arrayBufferToBase64(bytes.buffer);
}

function arrayBufferToBase64(buffer: ArrayBufferLike): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return globalThis.btoa(binary);
}

export interface InspectResult {
  status: 'ok' | 'error';
  found: boolean;
  data: {
    'text/plain'?: string;
    'text/html'?: string;
  };
}

export interface EvaluateResult {
  status: 'ok' | 'error';
  value: string | null;
  type: string | null;
}
